// @flow

import { Component } from '~/sources/scripts/jsti/core';
import AsyncRequest from '../classes/AsyncRequest';
import Template from '../classes/Template';
import RatingModifier from '../classes/search/templateModifiers/RatingModifier';
import Notifications from '../constants/Notifications';

export default class FeaturedProductReview extends Component {
  #productId: string;
  allowedRatings = [4, 5];
  template: HTMLElement;

  /**
   * Instantiate the component
   */
  bind(el: HTMLElement): void {
    super.bind(el);

    const productId: string = this.el.dataset.productId;
    if (!productId) {
      throw new Error('A product is required to fetch the featured product');
    }
    this.#productId = productId;

    const template = this.el.querySelector('template[ref="featured-review"]');
    if (!template) {
      throw new Error('A template is required');
    }
    this.template = Template.getContent(template);

    this.handle();
  }

  /**
   * Handle fetching and rendering the featured product review
   */
  async handle(): Promise<Object | null> {
    const data = await this.featuredReview();
    this.render(data);
    this.notify(Notifications.UPDATE_FEATURED_REVIEW, { review: data });
  }

  /**
   * Get the featured review
   */
  async featuredReview(): Promise<Object|null> {
    const promises = this.allowedRatings.map((rating: number) => {
      return this.fetchReviews(rating);
    });

    let reviews = [];

    try {
      reviews = (await Promise.all(promises))
        .map((response: Object) => response.data.pagination.data)
        .flat()
        .sort((item: Object, comparison: Object) => {
          return new Date(comparison.created_at) - new Date(item.created_at);
        });
    } catch {}

    if (!reviews.length) return null;

    return reviews[0];
  }

  /**
   * Render the review
   */
  async render(data: ?Object): void {
    if (!data) {
      this.el.innerHTML = '';
      return;
    }

    const review = this.template.cloneNode(true);

    const title = review.getElementByRef('title');
    const content = review.getElementByRef('content');
    const author = review.getElementByRef('author');
    const date = review.getElementByRef('date');
    const rating = review.getElementByRef('rating');
    const ratingHeader = review.getElementByRef('rating-header');

    title.innerHTML = data.title;

    content.innerHTML = data.content;
    author.innerHTML = data.name;

    date.innerHTML = new Date(data.created_at).toLocaleDateString(window.locale, {
      day: 'numeric', month: 'long', year: 'numeric'
    });

    rating.remove();
    ratingHeader.insertBefore(new RatingModifier(rating).build(data.rating), title);

    this.el.innerHTML = review.outerHTML;
  }

  /**
   * Fetch reviews from our API by the given rating
   */
  async fetchReviews(rating: number): Promise<Object> {
    return AsyncRequest.get(`/product/${this.#productId}/reviews?rating=${rating}`);
  }
}
